import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function SettingsPage() {
  const { user, updateMe } = useAuth();
  const [form, setForm] = useState({
    handle: user?.handle ?? "",
    displayName: user?.display_name ?? "",
    bio: user?.bio ?? "",
    isCreator: !!user?.is_creator,
    creatorOpen: !!user?.creator_open,
    creatorTurnaroundDays: user?.creator_turnaround_days ?? "",
    creatorQueueNote: user?.creator_queue_note ?? "",
    paymentLink: user?.payment_link ?? "",
    avatarUrl: user?.avatar_url ?? "",
  });
  const [saving, setSaving] = useState(false);

  async function onAvatarUpload(file: File) {
    try {
      const up = await api.upload(file, { kind: "avatar" });
      setForm(f => ({ ...f, avatarUrl: up.url }));
      toast.success("Avatar uploaded");
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "upload failed");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMe({
        handle: form.handle,
        displayName: form.displayName || null,
        bio: form.bio || null,
        isCreator: form.isCreator,
        creatorOpen: form.creatorOpen,
        creatorTurnaroundDays: form.creatorTurnaroundDays ? Number(form.creatorTurnaroundDays) : null,
        creatorQueueNote: form.creatorQueueNote || null,
        paymentLink: form.paymentLink || null,
        avatarUrl: form.avatarUrl || null,
      });
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "save failed");
    } finally { setSaving(false); }
  }

  if (!user) return <div>Sign in to view settings.</div>;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>

      <Card>
        <CardHeader><CardTitle className="text-base">Profile</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Handle</Label>
            <Input value={form.handle} onChange={e => setForm({ ...form, handle: e.target.value.toLowerCase() })} />
            <p className="text-xs text-muted-foreground">Public URL: fanfix.me/{form.handle}</p>
          </div>
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <Input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Bio</Label>
            <Textarea rows={3} value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Avatar</Label>
            <div className="flex items-center gap-3">
              {form.avatarUrl && <img src={form.avatarUrl} alt="avatar" className="h-12 w-12 rounded-full object-cover" />}
              <input
                type="file"
                accept="image/*"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) onAvatarUpload(f);
                }}
                className="text-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Creator settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={form.isCreator}
              onCheckedChange={v => setForm({ ...form, isCreator: !!v })}
            />
            <span>
              <span className="block font-medium">I accept commissions</span>
              <span className="text-xs text-muted-foreground">Turn this on to publish services and receive requests.</span>
            </span>
          </label>
          {form.isCreator && (
            <>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={form.creatorOpen}
                  onCheckedChange={v => setForm({ ...form, creatorOpen: !!v })}
                />
                <span>
                  <span className="block font-medium">Currently open</span>
                  <span className="text-xs text-muted-foreground">Uncheck to show a waitlist badge on your page.</span>
                </span>
              </label>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Typical turnaround (days)</Label>
                  <Input type="number" value={form.creatorTurnaroundDays as any} onChange={e => setForm({ ...form, creatorTurnaroundDays: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Payment link (Stripe / Ko-fi / etc.)</Label>
                  <Input value={form.paymentLink} onChange={e => setForm({ ...form, paymentLink: e.target.value })} placeholder="https://…" />
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Queue note</Label>
                  <Textarea rows={2} value={form.creatorQueueNote} onChange={e => setForm({ ...form, creatorQueueNote: e.target.value })} placeholder="A short message shown on your public page (e.g. 'Opening 5 slots this week')." />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}
